import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Trip } from '../../types/models';
import { RootState } from '../store';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';

interface TripState {
  trips: Trip[];
  selectedTrip: Trip | null;
  loading: boolean;
  error: string | null;
}

const initialState: TripState = {
  trips: [],
  selectedTrip: null,
  loading: false,
  error: null,
};

export const fetchTrips = createAsyncThunk(
  'trips/fetchTrips',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/trips/');
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Yol kayıtları alınamadı'));
    }
  }
);

export const fetchTripById = createAsyncThunk(
  'trips/fetchTripById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/trips/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Yol kaydı alınamadı'));
    }
  }
);

export const createTrip = createAsyncThunk(
  'trips/createTrip',
  async (tripData: Partial<Trip>, { rejectWithValue }) => {
    try {
      const response = await api.post('/trips/', tripData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Yol kaydı oluşturulamadı'));
    }
  }
);

export const updateTrip = createAsyncThunk(
  'trips/updateTrip',
  async ({ id, tripData }: { id: number; tripData: Partial<Trip> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/trips/${id}/`, tripData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Yol kaydı güncellenemedi'));
    }
  }
);

export const deleteTrip = createAsyncThunk(
  'trips/deleteTrip',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/trips/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Yol kaydı silinemedi'));
    }
  }
);

const tripSlice = createSlice({
  name: 'trips',
  initialState,
  reducers: {
    clearSelectedTrip: (state) => {
      state.selectedTrip = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Trips
    builder
      .addCase(fetchTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrips.fulfilled, (state, action) => {
        state.loading = false;
        state.trips = action.payload;
      })
      .addCase(fetchTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Trip By Id
    builder
      .addCase(fetchTripById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTripById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTrip = action.payload;
      })
      .addCase(fetchTripById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Trip
    builder
      .addCase(createTrip.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrip.fulfilled, (state, action) => {
        state.loading = false;
        state.trips.push(action.payload);
      })
      .addCase(createTrip.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Trip
    builder
      .addCase(updateTrip.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrip.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.trips.findIndex((trip) => trip.id === action.payload.id);
        if (index !== -1) {
          state.trips[index] = action.payload;
        }
        state.selectedTrip = action.payload;
      })
      .addCase(updateTrip.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Trip
    builder
      .addCase(deleteTrip.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrip.fulfilled, (state, action) => {
        state.loading = false;
        state.trips = state.trips.filter((trip) => trip.id !== action.payload);
        if (state.selectedTrip?.id === action.payload) {
          state.selectedTrip = null;
        }
      })
      .addCase(deleteTrip.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedTrip, clearError } = tripSlice.actions;

export const selectTrips = (state: RootState) => state.trips.trips;
export const selectSelectedTrip = (state: RootState) => state.trips.selectedTrip;
export const selectTripLoading = (state: RootState) => state.trips.loading;
export const selectTripError = (state: RootState) => state.trips.error;

export default tripSlice.reducer; 